import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import { TableColumnHeader } from '@/components/ui/table-cells/TableColumnHeader';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { Link, router } from '@inertiajs/react';
import { type ReactNode } from 'react';
import { route } from 'ziggy-js';
import { BugReportSeverityBadge, BugReportStatusBadge } from './bug-report-badges';
import { type BugReportListItem } from './types';

interface BugReportTableProps {
    reports: BugReportListItem[];
    // Opening a report requires bug-reports.update; read-only viewers get a
    // non-clickable row so they never land on a 403.
    canOpen: boolean;
    canSeeAll: boolean;
    visibleColumns: Set<string>;
    columnFilters: Record<string, string[]>;
    columnFilterOptions: Record<string, Array<{ value: string; label: string }>>;
    onFilterChange: (column: string, values: string[]) => void;
    sortColumn: string | null;
    sortDirection: 'asc' | 'desc' | null;
    onSortChange: (column: string, direction: 'asc' | 'desc') => void;
    onColumnHide: (column: string) => void;
}

// Columns the backend can order by (see Admin\BugReportController::buildFilteredQuery).
const SORTABLE_COLUMNS = new Set(['public_id', 'status', 'module', 'created']);

export function BugReportTable({
    reports,
    canOpen,
    canSeeAll,
    visibleColumns,
    columnFilters,
    columnFilterOptions,
    onFilterChange,
    sortColumn,
    sortDirection,
    onSortChange,
    onColumnHide,
}: BugReportTableProps) {
    const { t } = useTranslations();

    const openReport = (id: string) => {
        if (canOpen) {
            router.visit(route('admin.bug-reports.show', id));
        }
    };

    const allColumns = [
        { id: 'public_id', label: t('bug_reports.table.id', 'ID') },
        { id: 'status', label: t('bug_reports.table.status', 'Status') },
        { id: 'severity', label: t('bug_reports.table.severity', 'Severity') },
        { id: 'module', label: t('bug_reports.table.module', 'Module') },
        { id: 'summary', label: t('bug_reports.table.summary', 'Summary') },
        ...(canSeeAll
            ? [
                  { id: 'reporter', label: t('bug_reports.table.reporter', 'Reporter') },
                  { id: 'assignee', label: t('bug_reports.table.assignee', 'Assignee') },
              ]
            : []),
        { id: 'created', label: t('bug_reports.table.created', 'Created') },
    ].filter((column) => visibleColumns.has(column.id));

    const renderCell = (report: BugReportListItem, columnId: string): ReactNode => {
        switch (columnId) {
            case 'public_id': {
                const label = report.public_id ?? report.id.slice(0, 8);
                return canOpen ? (
                    <Link
                        href={route('admin.bug-reports.show', report.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="hover:bg-accent text-foreground rounded px-2 py-1.5 text-left font-semibold transition-colors"
                    >
                        {label}
                    </Link>
                ) : (
                    <span className="font-semibold">{label}</span>
                );
            }
            case 'status':
                return <BugReportStatusBadge status={report.status} />;
            case 'severity':
                return <BugReportSeverityBadge severity={report.severity_final ?? report.severity_suggested} />;
            case 'module':
                return report.module ?? <span className="text-muted-foreground text-sm">—</span>;
            case 'summary':
                return <span className="block max-w-[320px] truncate">{report.description}</span>;
            case 'reporter':
                return report.reporter?.name ?? t('bug_reports.table.unknown_reporter', 'Unknown');
            case 'assignee':
                return (
                    report.assignee?.name ?? <span className="text-muted-foreground text-sm">{t('bug_reports.table.unassigned', 'Unassigned')}</span>
                );
            case 'created':
                return (
                    <span className="text-muted-foreground text-sm">
                        {report.created_at ? new Date(report.created_at).toLocaleDateString() : '—'}
                    </span>
                );
            default:
                return null;
        }
    };

    return (
        <div className="space-y-4">
            <div className="bg-card mb-4 items-center justify-between gap-3 rounded-xl border px-4 py-2.5">
                <Table>
                    <TableHeader>
                        <TableRow>
                            {allColumns.map((column) => {
                                const selectedFilters = columnFilters[column.id] || [];
                                const filterOptions = columnFilterOptions[column.id] || [];
                                const sortable = SORTABLE_COLUMNS.has(column.id);

                                return (
                                    <TableHead key={column.id}>
                                        <TableColumnHeader
                                            label={column.label}
                                            sortDirection={sortColumn === column.id ? sortDirection : null}
                                            onSortAscending={sortable ? () => onSortChange(column.id, 'asc') : undefined}
                                            onSortDescending={sortable ? () => onSortChange(column.id, 'desc') : undefined}
                                            onHide={() => onColumnHide(column.id)}
                                            filterOptions={filterOptions}
                                            selectedFilters={selectedFilters}
                                            onFilterChange={(values) => onFilterChange(column.id, values)}
                                            isFiltered={selectedFilters.length > 0}
                                        />
                                    </TableHead>
                                );
                            })}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {reports.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={allColumns.length} className="text-muted-foreground py-8 text-center">
                                    {t('bug_reports.table.empty', 'No bug reports found.')}
                                </TableCell>
                            </TableRow>
                        ) : (
                            reports.map((report) => (
                                <TableRow
                                    key={report.id}
                                    className={cn('transition-colors', canOpen && 'hover:bg-muted/50 cursor-pointer')}
                                    onClick={canOpen ? () => openReport(report.id) : undefined}
                                >
                                    {allColumns.map((column) => (
                                        <TableCell key={column.id}>{renderCell(report, column.id)}</TableCell>
                                    ))}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
